import { Grid, Paper, Typography, TextField, Button, Alert, Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useRecoilValue, useRecoilState } from 'recoil';
import { 
    currentSessionAtom,
    exportsFromCurrentSessionAtom,
    allExported,
    doNotAskAgainWhenExportingAtom,
} from '../state';
import { useState, useEffect } from "react";
import { useSessionApiClient } from "../api/sessions";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ConfirmationDialog from "./ConfirmationDialog";
import { NEEDS_REVIEW_STATUS } from "../constants";

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        width: '100%',
        padding: theme.spacing(2),
        minHeight: '100vh',
        '& > *': {
            width: '100%',
        }
    },
    paper: {
        padding: theme.spacing(5),
        textAlign: 'left',
    },
}));

const initialSessionOutputValues = {
    outputParquetFile: ""
}

const isValidInputPath = (value) => {
    if (value.length === 0) return "This field is required";
    if (!value.startsWith("gs://") && !value.startsWith("https://"))
        return "Path must start with gs:// or https://";
    return "";
};

const Summary = () => {
    const classes = useStyles();
    const [sessionOutputValues, setSessionOutputValues] = useState(
        initialSessionOutputValues
    );
    const [sessionOutputErrors, setSessionOutputErrors] = useState({
        outputParquetFile: "",
    });
    const [exportError, setExportError] = useState("");

    const sessionApiClient = useSessionApiClient();
    let currentSession = useRecoilValue(currentSessionAtom);

    const [outputParquetFileCount, setOutputParquetFileCount] = useState(0)
    const [openExportConfirmationDialog, setOpenExportConfirmationDialog] = useState(false);
    const [doNotAskAgainWhenExporting, setDoNotAskAgainWhenExporting] = useRecoilState(doNotAskAgainWhenExportingAtom);
    const handleInputChange = (e) => {
        if (e.currentTarget.id === "output-parquet-file") {
            setOutputParquetFileCount(outputParquetFileCount + 1)
        }

        const { name, value } = e.target;

        setSessionOutputValues({
            ...sessionOutputValues,
            [name]: value,
        });
    };

    const hasInputErrors = (sessionInputErrors) => {
        return (
            sessionOutputErrors.outputParquetFile.length > 0
        );
    };

    const [exports, setExports] = useRecoilState(exportsFromCurrentSessionAtom);

    useEffect(() => {
        setSessionOutputErrors({
            outputParquetFile: isValidInputPath(sessionOutputValues.outputParquetFile),
        });
        let interval = null;
        const f = async () => {
            if (!allExported(exports)) {
                let newExports = await sessionApiClient.getExports(currentSession.id);
                setExports(newExports);
            }
        };
        interval = setInterval(f, 5000);
        return () => { clearInterval(interval) };
        // eslint-disable-next-line
    }, [sessionOutputValues, exports]);

    const exportParquet = async (e) => {
        setOpenExportConfirmationDialog(false);
        e.preventDefault();
        try {
            let newExport = await sessionApiClient.exportParquet(
                currentSession.id, sessionOutputValues.outputParquetFile
            );
            let newExports = [newExport].concat(exports);
            setExports(newExports)
            setExportError("");
        } catch (err) {
            setExportError(err);
        }
    };

    const onIntentToExport = async (event) => {
        // check if there are any remaining subjects/merge proposals that need review
        const unReviewedMergeProposals = await sessionApiClient.getMergeProposalsWithSubjectNamesFromCurrentSession(currentSession.id, NEEDS_REVIEW_STATUS);
        const unReviewedSubjects = await sessionApiClient.getSubjectNamesFromSessionId(currentSession.id, NEEDS_REVIEW_STATUS);
        const isEverythingReviewed = unReviewedMergeProposals.length === 0 && unReviewedSubjects.length === 0;

        if (doNotAskAgainWhenExporting || isEverythingReviewed) {
            exportParquet(event);
        } else {
            setOpenExportConfirmationDialog(true);
        }
    };

    return (
        <Box>
            <ConfirmationDialog
                title={"Confirmation required"}
                content={`This session contains subjects and/or merge proposals that still need review. Do you wish to export anyway?`}
                open={openExportConfirmationDialog}
                onClose={() => setOpenExportConfirmationDialog(false)}
                onAccept={exportParquet}
                doNotAskAgain={doNotAskAgainWhenExporting}
                onDoNotAskAgainPreferenceChanged={(newValue) =>
                    setDoNotAskAgainWhenExporting(newValue)
                }
            />
            <Box>
                <Grid
                    container
                    spacing={0}
                    direction="column"
                    alignItems="center"
                    justifyContent="top"
                    className={classes.root}
                >
                    <Grid item xs={6}>
                        {exportError.length > 0 && (
                            <Alert severity="error">{exportError}</Alert>
                        )}
                        <Paper elevation={3} className={classes.paper}>
                            <Typography>ID: {currentSession.id}</Typography>
                            <Typography>Input Parquet File: {currentSession.inputParquetFile}</Typography>
                            <Typography>Image Base Path: {currentSession.imagesBasePath}</Typography>
                            <Typography>Created At: {new Date(currentSession.createdAt * 1000).toLocaleString("en-US")}</Typography>
                            <Typography>Updated At: {new Date(currentSession.updatedAt * 1000).toLocaleString("en-US")}</Typography>
                            <TextField
                                id="output-parquet-file"
                                label="Output parquet file"
                                inputProps={{ "aria-label": "output-parquet-file" }}
                                variant="standard"
                                onChange={handleInputChange}
                                name="outputParquetFile"
                                value={sessionOutputValues.outputParquetFile}
                                fullWidth
                                error={outputParquetFileCount > 0 && sessionOutputErrors.outputParquetFile.length > 0}
                                helperText={sessionOutputErrors.outputParquetFile}
                                placeholder="gs://path/to/export/images.parquet"
                            />
                            <Button
                                variant="contained"
                                size="large"
                                onClick={onIntentToExport}
                                disabled={hasInputErrors(sessionOutputErrors)}
                                sx={{ marginTop: "1em" }}
                            >
                                Export
                            </Button>
                            <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Export ID</TableCell>
                                        <TableCell align="left">Export Path</TableCell>
                                        <TableCell align="left">Status Message</TableCell>
                                        <TableCell align="left">Status</TableCell>
                                        <TableCell align="left">Exported At</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {exports.map((row) => (
                                        <TableRow
                                            key={row.id}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell component="th" scope="row">
                                                {row.id}
                                            </TableCell>
                                            <TableCell align="left">{row.exportPath}</TableCell>
                                            <TableCell align="left">{row.statusMessage}</TableCell>
                                            <TableCell align="left">{row.status}</TableCell>
                                            <TableCell align="left">{new Date(row.exportedAt * 1000).toLocaleString("en-US")}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    )
}

export default Summary;
